import type { LocaleObject } from "@nuxtjs/i18n";
import type { Locale } from "vue-i18n";
import type { RouteLocationNormalizedGeneric } from "vue-router";

/**
 * Locales and their configuration.
 *
 * Neither the `nuxt-i18n` module nor the `vue-i18n` module provide an easy way to get the config of the current locale
 * from `nuxt.config.ts` at runtime. As a workaround, we place the config here, so that we can simply import it, both
 * from `nuxt.config.ts` and from elsewhere.
 */
export const locales: VvLocaleObject[] = [
  {
    code: "en",
    language: "en",
    name: "English",
    file: "en.json",
    isCatchallLocale: true,
    defaultTid: "esv2001",
  },
  {
    code: "fr",
    language: "fr",
    name: "Français",
    file: "fr.json",
    defaultTid: "neg1979",
  },
  {
    code: "id",
    language: "id",
    name: "Bahasa Indonesia",
    file: "id.json",
    defaultTid: "esv2001",
  },
];

export const localeCodes = locales.map((l) => l.code);

/**
 * A map of locales by their code.
 */
export const localesByCode = new Map<string, VvLocaleObject>(
  locales.map((l) => [l.code, l]),
);

/**
 * A custom implementation of the function normally returned by `createLocaleFromRouteGetter` in `@nuxtjs/i18n`.
 *
 * This is needed because the implementation from `@nuxtjs/i18n` is not importable:
 * - `await import("../node_modules/@nuxtjs/i18n/dist/runtime/routing/utils.js")`
 *   fails with `Package import specifier "#build/i18n.options.mjs" is not defined`.
 * - `await import("@nuxtjs/i18n/dist/runtime/routing/utils.js")` fails with
 *   `Package subpath '...' is not defined by "exports" in .../package.json`.
 *
 * See https://github.com/nuxt-modules/i18n/discussions/3074
 *
 * This implementation is simpler than the original
 * because we can always assume that the `prefix` strategy is being used.
 */
export function getLocaleFromRoute(
  route: RouteLocationNormalizedGeneric,
): Locale | undefined {
  const prefix = route.path.split("/")[1];
  for (const locale of localeCodes) {
    if (locale === prefix) {
      return locale;
    }
  }
  return getLocaleFromRouteName(route.name);
}

export interface VvLocaleObject extends LocaleObject {
  /**
   * The ID of the default translation for this locale. The language of this
   * translation should be the same as the language of the user interface.
   */
  defaultTid: string;
}

export const defaultLocale = "en" satisfies Locale;

/**
 * Checks if the given string refers to an available locale.
 */
export function isAvailableLocaleCode(code: any): code is Locale {
  return localeCodes.includes(code as Locale);
}

/**
 * Get the locale suffix from a localized route name, e.g. `commentary-vids-type___fr`.
 */
export function getLocaleFromRouteName(name: unknown): Locale | undefined {
  const code = String(name ?? "").match(/___([a-z]{2})$/)?.[1];
  return isAvailableLocaleCode(code) ? code : undefined;
}
